import { useState, useEffect, useRef, useMemo } from "react";
import { HiArrowRight } from "react-icons/hi";
import { Button } from "flowbite-react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { useTranslation } from "@repo/core";
import { useReturns } from "../../../hooks/indicators/useReturns";
import LoadingMonthlyReturns from "./LoadingMonthlyReturns";

interface MonthlyReturnsAnalysisProps {
  className?: string;
  isLoading?: boolean;
}

/**
 * Monthly returns analysis component showing distribution and heatmap
 */
export const MonthlyReturnsAnalysisComponent = ({
  className = "",
  isLoading: externalIsLoading = false,
}: MonthlyReturnsAnalysisProps) => {
  const [showScrollOverlay, setShowScrollOverlay] = useState(false);
  const [hasUserDismissedOverlay, setHasUserDismissedOverlay] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { tStrategy, tUI, tChart } = useTranslation();

  // Use real returns data
  const { transformedMatrix, columnHeaders, sortedYears, isLoading, isError } =
    useReturns();

  // Combine internal and external loading states. A failed fetch must not keep
  // reporting as loading - the fallback below would otherwise never be reached
  // and the two cards would spin forever.
  const combinedIsLoading = !isError && (isLoading || externalIsLoading);

  // Transform matrix data for charts
  const chartData = useMemo(() => {
    // Get monthly columns and add YTD at the end for heatmap
    const monthlyColumns = columnHeaders.filter((col) => col !== "YTD");
    const heatmapColumns = [...monthlyColumns, "YTD"]; // Include YTD in heatmap
    const allReturns: number[] = [];

    sortedYears.forEach((year) => {
      const yearData = transformedMatrix[year];
      if (yearData) {
        monthlyColumns.forEach((month) => {
          const value = yearData[month];
          if (typeof value === "number" && !isNaN(value)) {
            allReturns.push(value);
          }
        });
      }
    });

    // Create heatmap data for ApexCharts (including YTD)
    const heatmapData = sortedYears.map((year) => {
      const yearData = transformedMatrix[year] || {};
      return heatmapColumns.map((column) => {
        const value = yearData[column];
        // Use a large sentinel value for missing data that we can target later
        // Values are already converted to percentage in useReturns hook
        return {
          x: column,
          y: typeof value === "number" && !isNaN(value) ? value : -999999,
        };
      });
    });

    // Debug logging to check heatmap data structure
    console.log(
      "YTD Debug - Each row structure:",
      heatmapData.map((rowData, index) => {
        const ytdCell = rowData.find((cell) => cell.x === "YTD");
        return {
          year: sortedYears[index],
          ytdValue: ytdCell?.y === null ? "missing" : ytdCell?.y,
          totalCells: rowData.length,
          validCells: rowData.filter((cell) => cell.y !== null).length,
        };
      })
    );

    // Calculate distribution statistics
    const positiveCount = allReturns.filter((r) => r > 0).length;
    const negativeCount = allReturns.filter((r) => r < 0).length;
    const extremeCount = allReturns.filter((r) => Math.abs(r) > 0.05).length; // >5% extreme

    // Find most frequent return (rounded to nearest percent)
    const returnBuckets: Record<number, number> = {};
    allReturns.forEach((ret) => {
      const bucket = Math.round(ret);
      returnBuckets[bucket] = (returnBuckets[bucket] || 0) + 1;
    });

    const mostFrequentBucket = Object.entries(returnBuckets).reduce(
      (max, [bucket, count]) =>
        count > max.count ? { bucket: parseInt(bucket), count } : max,
      { bucket: 0, count: 0 }
    );

    return {
      allReturns,
      heatmapData,
      months: heatmapColumns, // Use heatmapColumns which includes YTD
      years: sortedYears.map(String),
      distribution: {
        mostFrequentReturn: `${mostFrequentBucket.bucket.toFixed(1)}%`,
        positiveMonths: positiveCount,
        negativeMonths: negativeCount,
        extremeReturns: extremeCount,
      },
    };
  }, [transformedMatrix, columnHeaders, sortedYears]);

  // Check if content has scrollbar and show overlay (only if user hasn't dismissed it)
  useEffect(() => {
    const checkForScrollbar = () => {
      if (
        scrollContainerRef.current &&
        chartData.heatmapData.length > 0 &&
        !hasUserDismissedOverlay
      ) {
        const hasHorizontalScrollbar =
          scrollContainerRef.current.scrollWidth >
          scrollContainerRef.current.clientWidth;

        if (hasHorizontalScrollbar && !showScrollOverlay) {
          setShowScrollOverlay(true);
        } else if (!hasHorizontalScrollbar && showScrollOverlay) {
          setShowScrollOverlay(false);
        }
      }
    };

    // Check for scrollbar after a short delay to ensure content is rendered
    const timer = setTimeout(checkForScrollbar, 500);

    return () => clearTimeout(timer);
  }, [chartData.heatmapData, hasUserDismissedOverlay, showScrollOverlay]);

  // Add CSS to immediately hide sentinel value cells during render
  useEffect(() => {
    const styleId = "heatmap-sentinel-values-hide";
    const existingStyle = document.getElementById(styleId);

    if (!existingStyle) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        /* Immediately hide cells that contain sentinel values - this prevents flashing during resize */
        .apexcharts-heatmap .apexcharts-datalabels text[x]:empty,
        .apexcharts-heatmap .apexcharts-datalabels text:not([x]) {
          display: none !important;
        }
        
        /* Hide cells with extreme negative values that fall outside our color scale */
        .apexcharts-heatmap .apexcharts-series rect[fill="#000000"],
        .apexcharts-heatmap .apexcharts-series rect[fill="rgb(0, 0, 0)"],
        .apexcharts-heatmap .apexcharts-series rect[fill="#000000"],
        .apexcharts-heatmap .apexcharts-series rect[fill="rgb(0, 0, 0)"],
        .apexcharts-heatmap .apexcharts-series rect[fill="transparent"],
        .apexcharts-heatmap .apexcharts-series rect:not([fill]) {
          opacity: 0 !important;
          pointer-events: none !important;
          display: none !important;
        }

        /* Hide any data labels that might show the sentinel value */
        .apexcharts-heatmap .apexcharts-datalabels text {
          visibility: visible;
        }
        .apexcharts-heatmap .apexcharts-datalabels text:empty {
          visibility: hidden !important;
        }

        text.apexcharts-datalabel {
          fill: #111827 !important;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      const style = document.getElementById(styleId);
      if (style) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Calculate dynamic distribution buckets based on real data
  const calculateDynamicDistribution = () => {
    if (chartData.allReturns.length === 0) {
      return {
        categories: [],
        returns: [],
      };
    }

    const allReturns = chartData.allReturns;
    const bucketCount = 10;

    // Calculate range from actual data
    const minValue = Math.min(...allReturns);
    const maxValue = Math.max(...allReturns);
    const range = maxValue - minValue;
    const bucketSize = range / bucketCount;

    // Create buckets with labels
    const categories: string[] = [];
    const returns: number[] = [];

    // Create buckets and count actual occurrences
    for (let i = 0; i < bucketCount; i++) {
      const bucketStart = minValue + i * bucketSize;
      const bucketEnd =
        i === bucketCount - 1 ? maxValue : bucketStart + bucketSize;

      // Create label for this bucket
      const label = `${bucketStart.toFixed(1)}% to ${bucketEnd.toFixed(1)}%`;
      categories.push(label);

      // Count actual values in this bucket
      const frequency = allReturns.filter((value) =>
        i === bucketCount - 1
          ? value >= bucketStart && value <= bucketEnd
          : value >= bucketStart && value < bucketEnd
      ).length;

      returns.push(frequency);
    }

    return {
      categories,
      returns,
    };
  };

  const dynamicDistribution = calculateDynamicDistribution();

  // Monthly returns distribution chart configuration
  const distributionChartOptions: ApexOptions = {
    chart: {
      type: "bar",
      height: 350,
      toolbar: {
        show: false,
      },
      background: "transparent",
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: "60%",
      },
    },
    colors: ["#3B82F6"], // Single blue color for all bars
    xaxis: {
      categories: dynamicDistribution.categories,
      labels: {
        rotate: -45,
        style: {
          fontSize: "12px",
        },
      },
    },
    yaxis: {
      title: {
        text: tChart("frequencyMonths"),
      },
      labels: {
        formatter: (value) => Math.round(value).toString(),
      },
    },
    grid: {
      borderColor: "#e5e7eb",
    },
    legend: {
      show: false,
    },
    tooltip: {
      y: {
        formatter: (value) => `${value} ${tUI("monthsLower")}`,
      },
    },
    dataLabels: {
      enabled: false,
    },
  };

  const distributionChartSeries = [
    {
      name: tChart("frequencyLabel"),
      data: dynamicDistribution.returns,
    },
  ];

  // Calculate dynamic color ranges based on real data
  const calculateDynamicColorRanges = () => {
    const shadesOfRed = ["#EA7070", "#EF8C8C", "#F4A6A6", "#F8C8C8", "#FDE2E2"];
    const shadesOfGreen = [
      "#E6F4EA",
      "#C8EBCF",
      "#A8E0B7",
      "#8FD7A4",
      "#74CD8E",
    ];

    // Collect all values including YTD for color scale calculation (exclude sentinel values)
    const allValues: number[] = [];

    sortedYears.forEach((year) => {
      const yearData = transformedMatrix[year];
      if (yearData) {
        // Include all columns (monthly + YTD) for color scale
        Object.keys(yearData).forEach((column) => {
          const value = yearData[column];
          if (typeof value === "number" && !isNaN(value) && value !== -999999) {
            allValues.push(value);
          }
        });
      }
    });

    if (allValues.length === 0) {
      // Fallback to default ranges if no data
      return [
        { from: -20, to: 0, color: shadesOfRed[5] },
        { from: 0, to: 20, color: shadesOfGreen[5] },
      ];
    }

    // Separate positive and negative values
    const negativeValues = allValues.filter((v) => v < 0);
    const positiveValues = allValues.filter((v) => v > 0);
    const ranges = [];

    // Handle negative values with shadesOfRed
    if (negativeValues.length > 0) {
      const minNegative = Math.min(...negativeValues);
      const negativeRange = 0 - minNegative; // Range from minNegative to 0
      const bucketSize = negativeRange / shadesOfRed.length;

      for (let i = 0; i < shadesOfRed.length; i++) {
        const from = minNegative + i * bucketSize;
        const to =
          i === shadesOfRed.length - 1
            ? 0 // Last negative bucket goes to 0
            : minNegative + (i + 1) * bucketSize;

        ranges.push({
          from,
          to,
          color: shadesOfRed[i],
        });
      }
    }

    // Handle positive values with shadesOfGreen
    if (positiveValues.length > 0) {
      const maxPositive = Math.max(...positiveValues);
      const positiveRange = maxPositive - 0; // Range from 0 to maxPositive
      const bucketSize = positiveRange / shadesOfGreen.length;

      for (let i = 0; i < shadesOfGreen.length; i++) {
        const from =
          i === 0
            ? 0 // First positive bucket starts from 0
            : 0 + i * bucketSize;
        const to =
          i === shadesOfGreen.length - 1
            ? maxPositive
            : 0 + (i + 1) * bucketSize;

        ranges.push({
          from,
          to,
          color: shadesOfGreen[i],
        });
      }
    }

    return ranges;
  };

  // Heatmap chart configuration
  const heatmapChartOptions: ApexOptions = {
    chart: {
      type: "heatmap",
      height: 400,
      toolbar: {
        show: false,
      },
      background: "transparent",
      events: {
        mounted: (chartContext: { el: HTMLElement }) => {
          // Hide sentinel value cells immediately and after a short delay
          const hideSentinelCells = () => {
            const chart = chartContext.el;
            const allRects = chart.querySelectorAll(".apexcharts-heatmap-rect");

            allRects.forEach((rect: Element) => {
              const htmlRect = rect as HTMLElement;

              // Get the data-value attribute or check if the rect has a sentinel value
              const dataValue = htmlRect.getAttribute("data-value");
              const dataOriginalValue = htmlRect.getAttribute(
                "data-original-value"
              );

              // Check various ways ApexCharts might store the value
              if (
                dataValue === "-999999" ||
                dataOriginalValue === "-999999" ||
                htmlRect.textContent === "-999999" ||
                htmlRect.getAttribute("val") === "-999999"
              ) {
                htmlRect.style.display = "none";
                htmlRect.style.pointerEvents = "none";
                htmlRect.style.opacity = "0";
              }

              // Alternative: check if the fill color suggests this is outside our color scale
              const fill = htmlRect.getAttribute("fill");
              if (
                fill &&
                (fill === "#000000" ||
                  fill === "transparent" ||
                  fill === "none")
              ) {
                // These colors might indicate sentinel values
                htmlRect.style.display = "none";
                htmlRect.style.pointerEvents = "none";
                htmlRect.style.opacity = "0";
              }
            });

            // Also hide any data labels showing sentinel values
            const dataLabels = chart.querySelectorAll(
              ".apexcharts-datalabels text"
            );
            dataLabels.forEach((label: Element) => {
              const htmlLabel = label as HTMLElement;
              if (
                htmlLabel.textContent === "-999999.0%" ||
                htmlLabel.textContent === "-999999%" ||
                htmlLabel.textContent === "-999999"
              ) {
                htmlLabel.style.display = "none";
              }
            });
          };

          // Run immediately
          hideSentinelCells();

          // Run again after a short delay to catch any late renders
          setTimeout(hideSentinelCells, 50);
          setTimeout(hideSentinelCells, 200);
        },
        updated: (chartContext: { el: HTMLElement }) => {
          // Hide sentinel value cells immediately when chart is updated (like during resize)
          const hideSentinelCells = () => {
            const chart = chartContext.el;
            const allRects = chart.querySelectorAll(".apexcharts-heatmap-rect");

            allRects.forEach((rect: Element) => {
              const htmlRect = rect as HTMLElement;

              // Get the data-value attribute or check if the rect has a sentinel value
              const dataValue = htmlRect.getAttribute("data-value");
              const dataOriginalValue = htmlRect.getAttribute(
                "data-original-value"
              );

              // Check various ways ApexCharts might store the value
              if (
                dataValue === "-999999" ||
                dataOriginalValue === "-999999" ||
                htmlRect.textContent === "-999999" ||
                htmlRect.getAttribute("val") === "-999999"
              ) {
                htmlRect.style.display = "none";
                htmlRect.style.pointerEvents = "none";
                htmlRect.style.opacity = "0";
              }

              // Alternative: check if the fill color suggests this is outside our color scale
              const fill = htmlRect.getAttribute("fill");
              if (
                fill &&
                (fill === "#000000" ||
                  fill === "#ffffff" ||
                  fill === "transparent" ||
                  fill === "none")
              ) {
                // These colors might indicate sentinel values
                htmlRect.style.display = "none";
                htmlRect.style.pointerEvents = "none";
                htmlRect.style.opacity = "0";
              }
            });

            // Also hide any data labels showing sentinel values
            const dataLabels = chart.querySelectorAll(
              ".apexcharts-datalabels text"
            );
            dataLabels.forEach((label: Element) => {
              const htmlLabel = label as HTMLElement;
              if (
                htmlLabel.textContent === "-999999.0%" ||
                htmlLabel.textContent === "-999999%" ||
                htmlLabel.textContent === "-999999"
              ) {
                htmlLabel.style.display = "none";
              }
            });
          };

          // Run immediately on update (important for resize events)
          hideSentinelCells();

          // Run again after a very short delay to catch any late renders
          setTimeout(hideSentinelCells, 10);
          setTimeout(hideSentinelCells, 100);
        },
      },
    },
    plotOptions: {
      heatmap: {
        shadeIntensity: 0.5,
        radius: 4,
        useFillColorAsStroke: true,
        enableShades: false,
        distributed: false,
        colorScale: {
          ranges: calculateDynamicColorRanges(),
        },
      },
    },
    noData: {
      text: "",
      align: "center",
      verticalAlign: "middle",
      offsetX: 0,
      offsetY: 0,
      style: {
        color: "transparent",
        fontSize: "0px",
      },
    },
    fill: {
      type: "solid",
      opacity: 1,
    },
    stroke: {
      width: 1,
      colors: ["#000000"],
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "12px",
        fontWeight: "bold",
      },
      formatter: (value) => {
        if (value === null || value === undefined || value === -999999)
          return "";
        const numValue =
          typeof value === "number"
            ? value
            : typeof value === "string"
            ? parseFloat(value)
            : 0;
        if (isNaN(numValue) || numValue === -999999) return "";
        // Value is already in percentage format, no need to multiply by 100
        return `${numValue.toFixed(2)}%`;
      },
    },
    xaxis: {
      type: "category",
      categories: chartData.months,
      labels: {
        style: {
          fontSize: "12px",
        },
      },
    },
    grid: {
      show: false,
    },
    legend: {
      show: false,
    },
    tooltip: {
      enabled: true,
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        const value =
          w.globals.initialSeries[seriesIndex]?.data[dataPointIndex]?.y;
        if (
          value === null ||
          value === undefined ||
          isNaN(value) ||
          value === -999999
        ) {
          return ""; // Return empty string to disable tooltip for null/undefined/sentinel values
        }
        const numValue =
          typeof value === "number" ? value : parseFloat(value) || 0;
        const year = chartData.years[seriesIndex];
        const month = chartData.months[dataPointIndex];
        return `
          <div class="bg-gray-900 text-white text-xs rounded px-2 py-1">
            <div class="font-semibold">${month} ${year}</div>
            <div>${numValue.toFixed(2)}%</div>
          </div>
        `;
      },
    },
  };

  const heatmapChartSeries = chartData.heatmapData.map((yearData, index) => {
    // Ensure each row has exactly 13 data points (12 months + YTD)
    const completeRowData = chartData.months.map((month) => {
      const existingData = yearData.find((point) => point.x === month);
      return existingData || { x: month, y: -999999 }; // Use sentinel value for missing data
    });

    return {
      name: chartData.years[index],
      data: completeRowData,
    };
  });

  if (combinedIsLoading) {
    return <LoadingMonthlyReturns className={className} />;
  }

  if (isError || !chartData.years.length) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="text-center py-12">
            <div className="text-gray-500">
              <div className="text-6xl mb-4">📊</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {tStrategy("monthlyReturnsNotAvailable")}
              </h3>
              <p className="text-gray-600">
                {isError
                  ? tStrategy("monthlyReturnsUnableToLoad")
                  : tStrategy("monthlyReturnsNoData")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Distribution of Monthly Returns Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {tStrategy("monthlyReturns")}
        </h2>

        {/* Horizontal divider */}
        <hr className="border-gray-200 mb-6" />

        {/* Monthly Returns Distribution Chart Section */}
        <div className="h-96">
          <Chart
            options={distributionChartOptions}
            series={distributionChartSeries}
            type="bar"
            height="100%"
          />
        </div>
      </div>

      {/* Monthly Returns Heatmap Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 w-full max-w-full overflow-hidden relative">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {tStrategy("monthlyReturnsHeatmapTitle")}
        </h2>

        {/* Horizontal divider */}
        <hr className="border-gray-200 mb-6" />

        {/* Scroll Overlay */}
        {showScrollOverlay && (
          <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center z-50 transition-opacity duration-500">
            <div className="bg-white rounded-lg shadow-lg p-4 border border-gray-200 max-w-xs mx-4">
              <div className="flex items-center space-x-2 text-blue-600 mb-2">
                <HiArrowRight size={16} />
                <span className="font-medium text-sm">
                  {tUI("scrollToExplore")}
                </span>
              </div>
              <p className="text-xs text-gray-600">
                {tUI("scrollToExploreBody")}
              </p>
              <div className="flex items-center justify-center mt-4">
                <Button
                  onClick={() => {
                    setShowScrollOverlay(false);
                    setHasUserDismissedOverlay(true);
                  }}
                >
                  <span>{tUI("iUnderstand")}</span>
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Heatmap Chart Section */}
        <div
          ref={scrollContainerRef}
          className="w-full overflow-x-auto overflow-y-hidden"
        >
          <div style={{ minWidth: "600px" }}>
            <div className="h-96">
              <Chart
                options={heatmapChartOptions}
                series={heatmapChartSeries}
                type="heatmap"
                height="100%"
                width={Math.max(800, (chartData.months?.length || 12) * 60)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyReturnsAnalysisComponent;
